/**
 * useEditorState - Core editor state management with autosave
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getApiUrl } from '@/config/api';

const API = getApiUrl();

export function useEditorState(projectId, getAuthHeaders) {
  const [editorState, setEditorState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // History for undo/redo
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const maxHistoryLength = 50;
  
  // Autosave
  const autosaveTimeoutRef = useRef(null);
  const lastSavedStateRef = useRef(null);
  const pendingChangesRef = useRef(false);

  // Load editor state
  const loadState = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/projects/${projectId}/editor`, {
        headers: getAuthHeaders()
      });
      const state = res.data.state;
      setEditorState(state);
      lastSavedStateRef.current = JSON.stringify(state);
      
      // Initialize history with current state
      setHistory([state]);
      setHistoryIndex(0);
      
      return { state, isNew: res.data.is_new };
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [projectId, getAuthHeaders]);

  // Save state to backend
  const saveState = useCallback(async (stateToSave) => {
    if (!stateToSave) return;
    
    const currentStateStr = JSON.stringify(stateToSave);
    if (currentStateStr === lastSavedStateRef.current) return;
    
    try {
      setSaving(true);
      await axios.put(`${API}/projects/${projectId}/editor`, {
        video_clips: stateToSave.video_clips,
        audio_tracks: stateToSave.audio_tracks,
        playhead_position_ms: stateToSave.playhead_position_ms,
        zoom_level: stateToSave.zoom_level
      }, {
        headers: getAuthHeaders()
      });
      lastSavedStateRef.current = currentStateStr;
      pendingChangesRef.current = false;
    } catch (err) {
      console.error('Autosave failed:', err);
    } finally {
      setSaving(false);
    }
  }, [projectId, getAuthHeaders]);

  // Trigger autosave with debounce
  const triggerAutosave = useCallback(() => {
    pendingChangesRef.current = true;
    
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }
    
    autosaveTimeoutRef.current = setTimeout(() => {
      if (editorState) {
        saveState(editorState);
      }
      autosaveTimeoutRef.current = null;
    }, 3000);
  }, [editorState, saveState]);

  // Update state and add to history
  const updateState = useCallback((updater) => {
    setEditorState(prev => {
      const newState = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      
      // Add to history (truncate future if we're not at the end)
      setHistory(h => {
        const newHistory = h.slice(0, historyIndex + 1);
        newHistory.push(newState);
        // Limit history length
        if (newHistory.length > maxHistoryLength) {
          newHistory.shift();
          return newHistory;
        }
        return newHistory;
      });
      setHistoryIndex(i => Math.min(i + 1, maxHistoryLength - 1));
      
      return newState;
    });
    triggerAutosave();
  }, [historyIndex, triggerAutosave]);

  // Update video clips
  const updateVideoClips = useCallback((clips) => {
    updateState(prev => ({
      ...prev,
      video_clips: clips,
      duration_ms: clips.reduce((max, clip) => 
        Math.max(max, clip.start_ms + clip.duration_ms), 0)
    }));
  }, [updateState]);

  // Update audio tracks
  const updateAudioTracks = useCallback((tracks) => {
    updateState(prev => ({ ...prev, audio_tracks: tracks }));
  }, [updateState]);

  // Undo
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      setHistoryIndex(i => i - 1);
      setEditorState(history[historyIndex - 1]);
      triggerAutosave();
    }
  }, [history, historyIndex, triggerAutosave]);

  // Redo
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(i => i + 1);
      setEditorState(history[historyIndex + 1]);
      triggerAutosave();
    }
  }, [history, historyIndex, triggerAutosave]);

  // Check if we have pending changes
  const hasPendingChanges = useCallback(() => {
    return pendingChangesRef.current || autosaveTimeoutRef.current !== null;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
        // Force save on unmount
        if (editorState && pendingChangesRef.current) {
          saveState(editorState);
        }
      }
    };
  }, [editorState, saveState]);

  return {
    editorState,
    setEditorState,
    loading,
    saving,
    error,
    loadState,
    saveState,
    updateState,
    updateVideoClips,
    updateAudioTracks,
    undo,
    redo,
    canUndo: historyIndex > 0,
    canRedo: historyIndex < history.length - 1,
    hasPendingChanges,
    triggerAutosave
  };
}
